import styled from '@emotion/styled';

export const ModalOverlay = styled.div<{ isOpen: boolean; $loggedIn?: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	/* 로그인 시 헤더·UserPanel과 동색 — 패딩 구간만 회색이 되지 않게 그라데이션 */
	background: ${(props) =>
		props.$loggedIn
			? `linear-gradient(
		180deg,
		${props.theme.colors.primary400} 0,
		${props.theme.colors.primary400} calc(${props.theme.heights.headerMobile} + env(safe-area-inset-top, 0px)),
		${props.theme.colors.primary50} calc(${props.theme.heights.headerMobile} + env(safe-area-inset-top, 0px))
	)`
			: props.theme.colors.primary50};
	display: flex;
	flex-direction: column;
	align-items: stretch;
	/* 고정 헤더(#header, z-index 더 높음) 아래에서 패널이 시작하도록 — 로고·이름 세로 겹침 방지 */
	padding-top: calc(${(props) => props.theme.heights.headerMobile} + env(safe-area-inset-top, 0px));
	box-sizing: border-box;
	opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
	visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
	/* 닫혀 있을 때도 z-index 99로 문서 위에 있으므로, 클릭이 헤더를 가리지 않게 함 */
	pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
	transition: opacity 0.25s ease, visibility 0.25s ease;
	z-index: 99;
	height: 100dvh;
	max-height: 100dvh;
	overflow: auto;
	-webkit-overflow-scrolling: touch;
`;
/** 비로그인 최상단 — 카드형으로 구분 */
export const GuestPanel = styled.div`
	flex-shrink: 0;
	margin: 0 1.2rem 0.8rem;
	padding: 1.35rem 1.4rem 1.5rem;
	padding-top: 1.25rem;
	background: ${(props) => props.theme.colors.surfacePage};
	border: 1px solid ${(props) => props.theme.colors.primary100};
	border-radius: ${(props) => props.theme.borderRadius.md};
	box-shadow: 0 2px 12px rgba(18, 83, 158, 0.07);
`;
export const GuestTitle = styled.p`
	margin: 0 0 0.8rem;
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	color: ${(props) => props.theme.colors.textSecondary};
	line-height: 1.45;
	letter-spacing: -0.02em;
`;
export const GuestActions = styled.div`
	display: flex;
	flex-direction: row;
	gap: 0.8rem;
`;
export const GuestButton = styled.button<{ $primary?: boolean }>`
	flex: 1;
	padding: 0.75rem 1rem;
	border-radius: ${(props) => props.theme.borderRadius.control};
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	cursor: pointer;
	transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
	${(props) =>
		props.$primary
			? `
		border: none;
		background: ${props.theme.colors.primary400};
		color: #fff;
		&:active {
			background: ${props.theme.colors.primaryHover};
		}
	`
			: `
		border: 1px solid ${props.theme.colors.borderDefault};
		background: ${props.theme.colors.surfacePage};
		color: ${props.theme.colors.textPrimary};
		&:active {
			background: ${props.theme.colors.surfaceHover};
		}
	`}
`;

/** 로그인 시 상단 회원 영역 — 배경은 기존 로그아웃 버튼색(primary400)과 동일 */
export const UserPanel = styled.div`
	flex-shrink: 0;
	padding: 1.1rem 2rem 1.2rem;
	padding-top: 1rem;
	background: ${(props) => props.theme.colors.primary400};
	border-bottom: 1px solid ${(props) => props.theme.colors.primaryHover};
`;
/** 이름 + 로그아웃 한 줄 — 나란히 붙여 배치 */
export const UserPanelTop = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
	flex-wrap: nowrap;
	gap: 1rem;
	min-width: 0;
`;
export const UserNameText = styled.div`
	margin: 0;
	flex: 0 1 auto;
	min-width: 0;
	max-width: calc(100% - 9rem);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: 1.4;
	letter-spacing: -0.02em;
	color: #fff;
	strong {
		font-size: inherit;
		font-weight: ${(props) => props.theme.fontWeight.weightSemi};
		color: #fff;
	}
	span {
		color: rgba(255, 255, 255, 0.88);
		font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	}
`;
/** 파란 패널 위에서는 흰 버튼으로 구분 */
export const LogoutPill = styled.button`
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.55rem 1rem;
	border: none;
	border-radius: ${(props) => props.theme.borderRadius.control};
	background: #fff;
	color: ${(props) => props.theme.colors.primary400};
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 1.3;
	cursor: pointer;
	transition: background 0.2s ease, opacity 0.2s ease;
	&:active {
		background: rgba(255, 255, 255, 0.92);
		opacity: 0.98;
	}
`;

export const SectionLabel = styled.h2`
	margin: 0;
	padding: 1.1rem 1.2rem 0.5rem;
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 1.35;
	letter-spacing: -0.03em;
	text-transform: none;
	color: ${(props) => props.theme.colors.primary400};
	background: transparent;
`;

export const MypageList = styled.div`
	display: flex;
	flex-direction: column;
	margin: 0 1.2rem 1.6rem;
	border-radius: ${(props) => props.theme.borderRadius.md};
	border: 1px solid ${(props) => props.theme.colors.primary100};
	background: ${(props) => props.theme.colors.surfacePage};
	box-shadow: 0 2px 10px rgba(18, 83, 158, 0.06);
	overflow: hidden;
`;
export const MypageLink = styled.button`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	margin: 0;
	padding: 1.2rem 1.4rem;
	border: none;
	border-top: 1px solid ${(props) => props.theme.colors.primary100};
	background: ${(props) => props.theme.colors.surfacePage};
	color: ${(props) => props.theme.colors.primary400};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightMedium};
	line-height: 1.45;
	letter-spacing: -0.02em;
	text-align: left;
	cursor: pointer;
	transition: background 0.15s ease;
	&:first-of-type {
		border-top: none;
	}
	&:active {
		background: ${(props) => props.theme.colors.primary50};
	}
	span {
		flex: 1;
		min-width: 0;
	}
`;

/** 마이페이지 ↔ 협회 메뉴 구분 */
export const MenuSectionDivider = styled.div`
	width: 100%;
	height: 0.8rem;
	flex-shrink: 0;
	background: transparent;
`;

export const SiteMenuWrap = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
	padding: 0 1.2rem 1.6rem;
	background: transparent;
`;

export const ModalContent = styled.div`
	display: flex;
	width: 100%;
	margin: 0;
	padding: 0;
	flex-direction: column;
	gap: 0.5rem;
`;
export const MainMenu = styled.div`
	color: ${(props) => props.theme.colors.primary400};
	font-size: ${(props) => props.theme.fontSize.sizeMd};
	font-weight: ${(props) => props.theme.fontWeight.weightSemi};
	line-height: 1.4;
	letter-spacing: -0.02em;
	text-transform: none;
	text-decoration: none;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	width: 100%;
	padding: 1.15rem 1.4rem;
	cursor: pointer;
	background: ${(props) => props.theme.colors.surfacePage};
	border: 1px solid ${(props) => props.theme.colors.primary100};
	border-radius: ${(props) => props.theme.borderRadius.md};
	box-shadow: 0 1px 4px rgba(18, 83, 158, 0.05);
	transition:
		background 0.15s ease,
		box-shadow 0.15s ease;
	&:active {
		background: ${(props) => props.theme.colors.primary50};
		box-shadow: 0 1px 6px rgba(18, 83, 158, 0.08);
	}
	a {
		color: inherit;
		text-decoration: none;
		width: 100%;
	}
`;
export const ModalSummary = styled.summary``;
export const SubMenu = styled.div`
	margin: 0 0 0.5rem;
	padding: 0;
	color: ${(props) => props.theme.colors.textSecondary};
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-weight: ${(props) => props.theme.fontWeight.weightRegular};
	line-height: 1.45;
	letter-spacing: -0.02em;
	text-transform: none;
	background: ${(props) => props.theme.colors.primary50};
	border: 1px solid ${(props) => props.theme.colors.primary100};
	border-radius: ${(props) => props.theme.borderRadius.md};
	overflow: hidden;
`;
export const MainTextWithIcon = styled.div`
	display: flex;
	width: 100%;
`;
export const TextWithIcon = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: space-between;
	gap: 0.8rem;
	cursor: pointer;
	padding: 1rem 1.25rem;
	transition: background 0.15s ease;
	color: ${(props) => props.theme.colors.textPrimary};
	&:active {
		background: ${(props) => props.theme.colors.primary50};
	}
`;
export const NavLinks = styled.div`
	display: flex;
	align-items: center;
	gap: 4rem;
	white-space: nowrap;
	padding-left: 2rem;
	@media (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		display: none;
	}
`;
export const NavMoLinks = styled.div`
	display: flex;
	align-items: center;
	gap: 1.2rem;
	white-space: nowrap;
	padding-left: 2rem;
	@media (min-width: ${(props) => props.theme.breakpoints.layout + 1}px) {
		display: none;
	}
`;
export const NavMoLink = styled.div`
	color: #fff;
	font-size: ${(props) => props.theme.fontSize.sizeXs};
	font-style: normal;
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: 130%;
	letter-spacing: -0.6px;
`;
export const NavMoLinkLogin = styled.a`
	color: #fff;
	font-size: ${(props) => props.theme.fontSize.sizeXs};
	font-style: normal;
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: 130%;
	letter-spacing: -0.6px;
	padding: 0.6rem;
`;
export const NavLink = styled.a`
	color: #fff;
	font-size: ${(props) => props.theme.fontSize.sizeLg};
	font-style: normal;
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: 130%;
	letter-spacing: -0.6px;
`;
export const ButtonGroup = styled.div`
	display: flex;
	gap: 1rem;
`;
export const Button = styled.button<{ primary?: boolean }>`
	color: #fff;
	font-size: ${(props) => props.theme.fontSize.sizeSm};
	font-style: normal;
	font-weight: ${(props) => props.theme.fontWeight.weightBold};
	line-height: 4rem;
	text-transform: upper case;
	padding: 0px 2.5rem 0px 2.3rem;
	border-radius: ${(props) => props.theme.borderRadius.panel};
	display: flex;
	align-items: center;
	gap: 0.5rem;
	cursor: pointer;
`;
export const BoundaryLine = styled.div<{ primary?: boolean }>`
	background: #e0dfe3;
`;
export const ImgBox = styled.div`
	width: 23.4rem;
	height: 7rem;
	@media screen and (max-width: ${(props) => props.theme.breakpoints.layout}px) {
		width: unset;
		height: 3.8rem;
	}
	img {
		width: 100%;
		height: 100%;
	}
`;
